import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { realEntityName } from '@pqrs/contracts';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { EstadoPendiente } from '@xiltrion/ui/bloques/estados';
import { Icono } from '@xiltrion/ui/icono';
import { useSession } from '@/app/session';
import { setupIssues, useSetupStatus } from './api';
import { SetupIssuesSheet } from './SetupIssuesSheet';

/**
 * Avisos del Administrador arriba de cada pantalla: la entidad en implementación (aún no recibe solicitudes) y los
 * requisitos obligatorios sin cumplir que no son pasos del asistente (correo, almacenamiento, motor…; en
 * operación, también los de los pasos). El detalle se abre en un panel lateral. No se muestra dentro de la puesta en marcha.
 */
export function SetupNotice() {
  const { me, admin } = useSession();
  const { pathname } = useLocation();
  const setup = me?.operation === 'SETUP';
  const q = useSetupStatus(!!me && admin);
  const [open, setOpen] = useState(false);
  if (!me || !admin || pathname.startsWith('/puesta-en-marcha')) return null;
  // En implementación los pasos del asistente se trabajan en la puesta en marcha: aquí solo lo que la entidad ya traía.
  const issues = setupIssues(q.data?.checks ?? [], { presetsOnly: setup });
  const n = issues.reduce((sum, g) => sum + g.checks.length, 0);
  return (
    <>
      {setup && (
        <Alert variant="info" className="mb-4">
          <Icono nombre="mdi:clipboard-list-outline" aria-hidden />
          <AlertTitle>La entidad está en implementación</AlertTitle>
          <AlertDescription>
            {q.data?.suite?.ready === false
              ? 'No recibe solicitudes. La puesta en marcha empieza cuando la entidad esté configurada en la suite.'
              : 'No recibe solicitudes hasta que completes la puesta en marcha y la pases a operación.'}
          </AlertDescription>
          <AlertAction><Button size="sm" asChild><Link to="/puesta-en-marcha">Continuar la puesta en marcha</Link></Button></AlertAction>
        </Alert>
      )}
      {n > 0 && (
        <Alert variant={setup ? 'warning' : 'destructive'} className="mb-4">
          <Icono nombre="mdi:alert-circle-outline" aria-hidden />
          <AlertTitle>{n === 1 ? 'Hay un requisito de configuración sin cumplir' : `Hay ${n} requisitos de configuración sin cumplir`}</AlertTitle>
          <AlertDescription>
            {issues.map((g) => g.label).join(', ')}. {setup ? 'No detiene la puesta en marcha, pero conviene resolverlo antes de operar.' : 'Mientras siga así, algunos casos pueden quedar sin responsable, sin correo al ciudadano o con plazos mal calculados.'}
          </AlertDescription>
          <AlertAction><Button size="sm" variant={setup ? 'outline' : 'default'} onClick={() => setOpen(true)}>Ver detalle</Button></AlertAction>
        </Alert>
      )}
      <SetupIssuesSheet open={open} onOpenChange={setOpen} issues={issues} />
    </>
  );
}

/**
 * Toda la vista de quien no administra mientras la entidad está en puesta en marcha (es solo del Administrador): sin
 * menú, solo «Cerrar sesión». Cuando pase a operación, `/auth/me` cambia y la app aparece sin recargar.
 */
export function PreparingPage() {
  const { me, askLogout } = useSession();
  return (
    <EstadoPendiente
      titulo="La entidad está en preparación"
      descripcion={`${realEntityName(me?.entity.name) ?? 'La entidad'} todavía está en la puesta en marcha de la plataforma. Cuando el administrador la termine y la pase a operación podrás trabajar tus casos aquí; no tienes que hacer nada mientras tanto.`}
      accion={<Button variant="outline" onClick={askLogout}><Icono nombre="mdi:logout" aria-hidden /> Cerrar sesión</Button>}
    />
  );
}
